import { useState } from 'react';
import {
  Trash2,
  DollarSign,
  TrendingDown,
  AlertTriangle,
  Plus,
  X,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  WASTE_REASON_LABELS,
  WASTE_TYPE_LABELS,
} from '@/features/waste';
import type {
  WasteReason,
  WasteType,
  WasteLog,
} from '@/features/waste';
import { toast } from 'sonner';
import {
  REASON_TABS,
  MOCK_VENUES,
  MOCK_ITEMS,
} from './waste-constants';
import { WasteGrid } from './waste-grid';

interface LogViewProps {
  readonly wasteLogs: WasteLog[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly stats: {
    totalLogs: number;
    totalCost: number;
    preConsumerCost: number;
    postConsumerCost: number;
  };
  readonly selectedReason: WasteReason | undefined;
  readonly setSelectedReason: (reason: WasteReason | undefined) => void;
  readonly onBulkUpload: (file: File) => void;
  readonly onRefetch: () => void;
}

export function LogView({
  wasteLogs,
  isLoading,
  error,
  stats,
  selectedReason,
  setSelectedReason,
  onBulkUpload,
  onRefetch,
}: LogViewProps) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    venueId: '',
    itemId: '',
    type: 'pre-consumer' as WasteType,
    reason: 'spoilage' as WasteReason,
    quantity: '',
    notes: '',
  });

  const selectedItem = MOCK_ITEMS.find((i) => i.id === formData.itemId);
  const estimatedCost = selectedItem ? Number(formData.quantity || 0) * selectedItem.costPerUnit : 0;

  const handleSubmitWaste = () => {
    if (!formData.venueId || !formData.itemId || !formData.quantity) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success('Waste logged successfully');
    setShowForm(false);
    setFormData({
      venueId: '',
      itemId: '',
      type: 'pre-consumer',
      reason: 'spoilage',
      quantity: '',
      notes: '',
    });
    onRefetch();
  };

  const exportData = wasteLogs.map((w) => ({
    wasteId: w.wasteId,
    venue: w.venueName,
    item: w.itemName,
    type: w.type,
    reason: w.reason,
    quantity: w.quantity,
    unit: w.unit,
    estimatedCost: w.estimatedCost,
    createdAt: w.createdAt,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Waste Log"
        entityNamePlural="Waste Logs"
        onAdd={() => setShowForm(true)}
        onBulkUpload={onBulkUpload}
        exportData={exportData}
        exportFilename="waste-logs.csv"
        canEdit={false}
        canDelete={false}
      />

      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {REASON_TABS.map((tab) => (
            <Button
              key={tab.label}
              variant={selectedReason === tab.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedReason(tab.value)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" /> Log Waste
        </Button>
      </div>

      {showForm && (
        <WasteForm
          formData={formData}
          setFormData={setFormData}
          selectedItem={selectedItem}
          estimatedCost={estimatedCost}
          onSubmit={handleSubmitWaste}
          onClose={() => setShowForm(false)}
        />
      )}

      <StatsCards stats={stats} />

      <WasteGrid
        wasteLogs={wasteLogs}
        isLoading={isLoading}
        error={error}
        onRefetch={onRefetch}
      />
    </>
  );
}

interface WasteFormProps {
  readonly formData: {
    venueId: string;
    itemId: string;
    type: WasteType;
    reason: WasteReason;
    quantity: string;
    notes: string;
  };
  readonly setFormData: (data: WasteFormProps['formData']) => void;
  readonly selectedItem: (typeof MOCK_ITEMS)[number] | undefined;
  readonly estimatedCost: number;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

function WasteForm({
  formData,
  setFormData,
  selectedItem,
  estimatedCost,
  onSubmit,
  onClose,
}: WasteFormProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Log New Waste</CardTitle>
          <CardDescription>Record food waste for tracking and analysis</CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Venue *</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.venueId}
              onChange={(e) => setFormData({
                ...formData,
                venueId: e.target.value,
              })}
            >
              <option value="">Select venue...</option>
              {MOCK_VENUES.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Item *</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.itemId}
              onChange={(e) => setFormData({
                ...formData,
                itemId: e.target.value,
              })}
            >
              <option value="">Select item...</option>
              {MOCK_ITEMS.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Type *</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.type}
              onChange={(e) => setFormData({
                ...formData,
                type: e.target.value as WasteType,
              })}
            >
              {Object.entries(WASTE_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reason *</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={formData.reason}
              onChange={(e) => setFormData({
                ...formData,
                reason: e.target.value as WasteReason,
              })}
            >
              {Object.entries(WASTE_REASON_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Quantity * {selectedItem ? `(${selectedItem.unit})` : ''}
            </label>
            <input
              type="number"
              min="0"
              step="0.1"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Enter quantity"
              value={formData.quantity}
              onChange={(e) => setFormData({
                ...formData,
                quantity: e.target.value,
              })}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Estimated Cost</label>
            <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm font-medium text-red-600">
              ${estimatedCost.toFixed(2)}
            </div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium">Notes</label>
            <textarea
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={2}
              placeholder="Optional notes about this waste..."
              value={formData.notes}
              onChange={(e) => setFormData({
                ...formData,
                notes: e.target.value,
              })}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit}>Log Waste</Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface StatsCardsProps {
  readonly stats: {
    totalLogs: number;
    totalCost: number;
    preConsumerCost: number;
    postConsumerCost: number;
  };
}

function StatsCards({ stats }: StatsCardsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total Waste Logs
          </CardTitle>
          <Trash2 className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalLogs}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Total Cost
          </CardTitle>
          <DollarSign className="h-5 w-5 text-red-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">
            ${stats.totalCost.toFixed(2)}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Pre-Consumer
          </CardTitle>
          <TrendingDown className="h-5 w-5 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-amber-600">
            ${stats.preConsumerCost.toFixed(2)}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Post-Consumer
          </CardTitle>
          <AlertTriangle className="h-5 w-5 text-purple-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-purple-600">
            ${stats.postConsumerCost.toFixed(2)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
